
import { useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  MoreHorizontal,
  Building,
  Users,
  Edit,
  Trash2
} from 'lucide-react';
import { Organization } from '@/types/auth';
import { EditOrganizationDialog } from './EditOrganizationDialog';
import { DeleteOrganizationDialog } from './DeleteOrganizationDialog';

interface OrganizationsTableProps {
  organizations: Organization[];
  onOrganizationUpdated: () => void;
}

export function OrganizationsTable({ organizations, onOrganizationUpdated }: OrganizationsTableProps) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  const handleEdit = (org: Organization) => {
    setSelectedOrganization(org);
    setEditDialogOpen(true);
  };

  const handleDelete = (org: Organization) => {
    setSelectedOrganization(org);
    setDeleteDialogOpen(true);
  };

  if (organizations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <Building className="h-12 w-12 text-muted-foreground mb-2" />
        <h3 className="text-lg font-medium">No organizations found</h3>
        <p className="text-center text-muted-foreground mt-1">
          Create your first organization to get started.
        </p>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Organization</TableHead>
            <TableHead>Domain</TableHead>
            <TableHead>Team Capacity</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-[80px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {organizations.map((org) => (
            <TableRow key={org.id}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center bg-primary/10 text-xs font-medium uppercase text-primary rounded">
                    {org.name.substring(0, 2)}
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium">{org.name}</span>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                {org.domain ? (
                  <Badge variant="outline">{org.domain}</Badge>
                ) : (
                  <span className="text-muted-foreground">No domain</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{org.max_team_members} members</span>
                </div>
              </TableCell>
              <TableCell>
                {new Date(org.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                      <span className="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(org)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit organization
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-destructive"
                      onClick={() => handleDelete(org)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete organization
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <EditOrganizationDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        organization={selectedOrganization}
        onOrganizationUpdated={onOrganizationUpdated}
      />

      <DeleteOrganizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        organization={selectedOrganization}
        onOrganizationDeleted={onOrganizationUpdated}
      />
    </>
  );
}
